import { useEffect, useRef, useState } from 'react';
import { HANDLE_MAX_LENGTH, validateHandle } from '@zip2live/shared/handles';
import { api } from './api.ts';

export type HandleStatus = { kind: 'idle' } | { kind: 'checking' } | { kind: 'ok'; handle: string } | { kind: 'bad'; message: string };

const HANDLE_PROBLEM = {
  too_short: 'At least 3 characters.',
  too_long: '24 characters at most.',
  invalid_characters: 'Letters, numbers, and - or _ between them.',
  reserved: 'That one is reserved. Try another.',
} as const;

/** Only characters a link can have; everything else never reaches the field. */
export function cleanHandleInput(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, HANDLE_MAX_LENGTH);
}

/**
 * Live availability as you type: shape and reserved words are checked instantly here, then the
 * server confirms nobody has it. Results are cached and stale answers are ignored. Nothing is held
 * for anyone until they claim it.
 */
export function useHandleCheck(handle: string, enabled = true): HandleStatus {
  const [status, setStatus] = useState<HandleStatus>({ kind: 'idle' });
  const checked = useRef(new Map<string, HandleStatus>());
  useEffect(() => {
    if (!enabled) return;
    const local = validateHandle(handle);
    if (!handle) return setStatus({ kind: 'idle' });
    if (!local.ok) return setStatus({ kind: 'bad', message: HANDLE_PROBLEM[local.reason] });
    const cached = checked.current.get(local.handle);
    if (cached) return setStatus(cached);
    setStatus({ kind: 'checking' });
    let stale = false;
    const t = setTimeout(() => {
      api.checkHandle(local.handle).then(
        (r) => {
          const next: HandleStatus = r.available ? { kind: 'ok', handle: local.handle } : { kind: 'bad', message: r.reason === 'taken' ? `zip2live.com/${local.handle} is taken. Try another.` : HANDLE_PROBLEM[r.reason as keyof typeof HANDLE_PROBLEM] ?? 'Try another link.' };
          checked.current.set(local.handle, next);
          if (!stale) setStatus(next);
        },
        () => !stale && setStatus({ kind: 'idle' }),
      );
    }, 250);
    return () => {
      stale = true;
      clearTimeout(t);
    };
  }, [handle, enabled]);
  return status;
}
